import { useMutation } from '@apollo/client';
import { deleteCookie } from '@do/seashell/utils';
import {
  Button,
  SpinnerButton,
  Text,
  TextInput,
  Tooltip,
  style
} from '@do/walrus';
import { Form, Formik } from 'formik';
import _get from 'lodash/get';
import React, { ReactElement } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import * as Yup from 'yup';
import { PreferredLoginProps } from '../../src/interfaces/login';
import { Variant, VariationTemplates } from '../../src/interfaces/variant';
import { teamVariant } from '../../src/login-variants';
import successIcon from '../assets/newBrandDesign/success-icon.svg';
import { createClient } from '../client';
import {
  FORCE_EXTERNAL,
  LOGIN_ENDPOINT,
  LOGIN_REDIRECT_URL
} from '../constants';
import copy from '../copy';
import {
  loginAccount as LoginAccount,
  loginAccountVariables
} from '../generated-types/loginAccount';
import { LoginType } from '../interfaces/login';
import { LOG_IN_ACCOUNT } from '../queries/queries';
import {
  addSessionData,
  handleFormError,
  saveSessionData
} from '../utils/gql-utils';
import { replaceInterpolatedVariables } from '../utils/string-utils';
import {
  ErrorBanner,
  GithubButton,
  GoogleButton,
  NoUnderlineLink,
  SignInButton
} from './Common';
import NewErrorBanner from './newBrandDesign/ErrorBanner';
import NewGithubButton from './newBrandDesign/GithubButton';
import NewGoogleButton from './newBrandDesign/GoogleButton';
import NewGoogleButtonWrapper from './newBrandDesign/GoogleButtonWrapper';
import NewLink from './newBrandDesign/Link';
import NewSpinnerButton from './newBrandDesign/SpinnerButton';
import NewSuccessBanner from './newBrandDesign/SuccessBanner';
import NewText from './newBrandDesign/Text';
import NewTextInput from './newBrandDesign/TextInput';
import NewTitle from './newBrandDesign/Title';
import NewWrapper from './newBrandDesign/Wrapper';
import newBrandDesignVars from './newBrandDesign/overwrite-vars.scss';

// Set AvatarProps to any because the native src attribute doesn't expect a possible null value
interface AvatarProps {
  src: string;
}

const NewFormHr = styled.hr`
  height: ${style.vars.borderWidth.thin};
  margin-bottom: 0;
  margin-top: ${style.vars.whitespace.medium};
  color: ${newBrandDesignVars.grey7};
`;

const FormHr = styled.h5`
  display: block;
  text-align: center;
  position: relative;
  margin-top: ${style.vars.space['3']};

  > span {
    color: ${style.colors.grey.dark};
    background: ${style.colors.white};
    padding: 0 ${style.vars.space['3']};
    text-transform: uppercase;
  }

  &:before {
    content: '';
    position: absolute;
    width: 100%;
    left: 0;
    top: calc(50% - 1px);
    border: ${style.vars.borderWidth.thin} solid ${style.colors.grey.light};
    z-index: -1;
  }

  + ${SignInButton} {
    margin-top: 0;
  }
`;

const SuccessBanner = styled.div`
  background: ${style.colors.green.lightest};
  padding: ${style.vars.whitespace.regular};
  border-radius: ${style.vars.borderRadius.base};
  border: ${style.vars.borderWidth.thin} solid ${style.colors.green.darkest};
  margin-bottom: ${style.vars.space['4']};
`;

const UserInfo = styled.div`
  display: flex;
  align-items: center;
  border-radius: ${style.vars.borderRadius.base};
  border: ${style.vars.borderWidth.thin} solid ${style.colors.grey.primary};
  margin-bottom: ${style.vars.space['3']};
  padding: ${style.vars.space['3']};
`;

const NewUserInfo = styled(UserInfo)`
  margin: ${style.vars.space['4']} 0;
  padding: ${style.vars.space['4']};
  border-radius: ${newBrandDesignVars.border_radius_xlarge};
  border-color: ${newBrandDesignVars.grey6};
`;

const Avatar = styled.img<AvatarProps>`
  width: ${style.vars.space['9']};
  height: ${style.vars.space['9']};
  border-radius: 50%;
  margin-right: ${style.vars.space['4']};
`;

const Email = styled(Text)`
  word-break: break-word;
`;

const NotYouWrap = styled.div`
  padding: ${style.vars.space['3']} 0 ${style.vars.space['1']};
`;

const ForgotPassword = styled.div`
  margin: ${style.vars.space['3']} 0 ${style.vars.space['2']};
  text-align: center;

  &:not(:last-child) {
    margin-top: ${style.vars.space['4']};
  }
`;

const ForgetPreferredSignInWrapper = styled.div`
  display: flex;
  flex: 1;
  justify-content: flex-end;
`;

const Wrapper = styled.div``;
const Title = styled.h4``;
const GoogleButtonWrapper = styled.div``;

const FormValidation = Yup.object().shape({
  email: Yup.string()
    .email(copy.form.validation.email_valid)
    .required(copy.form.validation.email_required),
  password: Yup.string().required(copy.form.validation.password_required)
});

const PreferredEmailValidation = Yup.object().shape({
  password: Yup.string().required(copy.form.validation.password_required)
});

interface Props {
  githubOauthUrl: string;
  googleOauthUrl: string;
  passwordSuccessBanner?: 'forgot' | 'reset';
  showNext: (page: LoginType) => void;
  user?: PreferredLoginProps;
  hasPreferredLogin: boolean;
  team?: {
    name: string;
  };
  variant: Variant;
  isNewBrandDesignEnabled?: boolean;
}
const client = createClient(LOGIN_ENDPOINT);

const LoginForm: React.FC<Props> = ({
  githubOauthUrl,
  googleOauthUrl,
  passwordSuccessBanner,
  showNext,
  user,
  hasPreferredLogin,
  team,
  variant,
  isNewBrandDesignEnabled
}): ReactElement => {
  const [genericServerError, setGenericServerError] = React.useState();
  const [isRedirecting, setIsRedirecting] = React.useState(false);
  const [loginAccount, { loading: isLoading }] = useMutation<
    LoginAccount,
    loginAccountVariables
  >(LOG_IN_ACCOUNT, { client });

  const navigate = useNavigate();

  const userEmail = _get(user, 'email');
  const hasPreferredEmail = _get(user, 'sign_in_method') === 'email';
  const hasPreferredGoogle = _get(user, 'sign_in_method') === 'google';
  const hasPreferredGithub = _get(user, 'sign_in_method') === 'github';
  const joinTeamCopy = replaceInterpolatedVariables(team, teamVariant);

  const PREFERRED_SIGN_IN_COOKIE = '_digitalocean_remember_me';
  const TEAM_INVITE_COOKIE = 'invite_code';

  const isNewBrand =
    isNewBrandDesignEnabled &&
    variant?.version === VariationTemplates.NEW_BRAND;
  const WrapperComponent = isNewBrand ? NewWrapper : Wrapper;
  const UserInfoComponent = isNewBrand ? NewUserInfo : UserInfo;
  const TitleComponent = isNewBrand ? NewTitle : Title;
  const LinkComponent = isNewBrand ? NewLink : NoUnderlineLink;
  const GoogleButtonWrapperComponent = isNewBrand
    ? NewGoogleButtonWrapper
    : GoogleButtonWrapper;
  const GithubButtonComponent = isNewBrand ? NewGithubButton : GithubButton;
  const GoogleButtonComponent = isNewBrand ? NewGoogleButton : GoogleButton;
  const SpinnerButtonComponent = isNewBrand ? NewSpinnerButton : SpinnerButton;
  const TextComponent = isNewBrand ? NewText : Text;
  const TextInputComponent = isNewBrand ? NewTextInput : TextInput;
  const ErrorBannerComponent = isNewBrand ? NewErrorBanner : ErrorBanner;
  const SuccessBannerComponent = isNewBrand ? NewSuccessBanner : SuccessBanner;

  return (
    <WrapperComponent>
      <Formik
        initialValues={{
          email: team ? userEmail : '',
          password: ''
        }}
        validationSchema={
          hasPreferredEmail ? PreferredEmailValidation : FormValidation
        }
        onSubmit={(values, { setErrors }) => {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          setGenericServerError(null as any); /* Reset displayed error */

          loginAccount({
            variables: {
              loginRequest: addSessionData(values)
            }
          })
            .then((successResponse) => {
              const {
                status,
                // eslint-disable-next-line @typescript-eslint/naming-convention
                session_data,
                // eslint-disable-next-line @typescript-eslint/naming-convention
                redirect_url,
                // eslint-disable-next-line @typescript-eslint/naming-convention
                required_next_step
              } = _get(successResponse, 'data.loginAccount');
              saveSessionData(session_data);

              if (status === 'granted') {
                setIsRedirecting(true);
                window.location.assign(redirect_url || LOGIN_REDIRECT_URL);
              } else if (status === 'pending') {
                showNext(required_next_step);
              } else if (status === 'rejected') {
                setGenericServerError(copy.errors.incorrect_email_or_password);
              }
            })
            .catch(handleFormError(setErrors, setGenericServerError));
        }}
      >
        {({ errors, values, touched }) => (
          <>
            {passwordSuccessBanner && (
              <SuccessBannerComponent>
                {isNewBrandDesignEnabled && <img src={successIcon} alt="" />}
                {copy.form.login.after_password[passwordSuccessBanner]}
              </SuccessBannerComponent>
            )}

            <TitleComponent>
              {team
                ? joinTeamCopy.title
                : hasPreferredLogin
                ? variant.preferredLoginTitle
                : variant.title}
            </TitleComponent>

            {team ? (
              <TextComponent>{joinTeamCopy.subtitle}</TextComponent>
            ) : (
              !hasPreferredLogin &&
              variant.subtitle && (
                <TextComponent>{variant.subtitle}</TextComponent>
              )
            )}

            {hasPreferredLogin && (
              <>
                <TextComponent>{variant.preferredLoginSubtitle}</TextComponent>
                <UserInfoComponent>
                  <Avatar src={_get(user, 'avatar_url') || ''} alt="avatar" />
                  <Email>{userEmail}</Email>
                  <ForgetPreferredSignInWrapper>
                    <Tooltip content="forget this sign-in" placement="left">
                      {
                        // eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex
                        <span tabIndex={0}>
                          {/* The above span is necessary for the Tooltip component. See CENG-1691 for more. */}
                          <Button
                            variation="icon"
                            icon="close"
                            onClick={() => {
                              // Delete remember me cookie
                              deleteCookie(PREFERRED_SIGN_IN_COOKIE);

                              // Delete invite_code cookie when choosing to sign in as another user
                              deleteCookie(TEAM_INVITE_COOKIE);

                              // Reload /login with cookie deleted to not display preferred login
                              window.location.reload();
                            }}
                          />
                        </span>
                      }
                    </Tooltip>
                  </ForgetPreferredSignInWrapper>
                </UserInfoComponent>
              </>
            )}

            {(!hasPreferredLogin || hasPreferredEmail) && (
              <Form autoComplete="none">
                {genericServerError && (
                  // eslint-disable-next-line @typescript-eslint/no-explicit-any
                  <ErrorBannerComponent error={genericServerError as any} />
                )}

                {hasPreferredLogin ? (
                  <TextInputComponent
                    placeholder={copy.form.login.password_placeholder}
                    required
                    name="password"
                    label={copy.form.password_label}
                    type="password"
                    touched={touched.password}
                    error={errors.password}
                    value={values.password}
                    autoComplete="none"
                    // eslint-disable-next-line jsx-a11y/no-autofocus
                    autoFocus
                    className={isNewBrand ? '' : 'do-u-mb--medium'}
                  />
                ) : (
                  <>
                    <TextInputComponent
                      placeholder={copy.form.email_placeholder}
                      required
                      name="email"
                      label={
                        isNewBrand
                          ? copy.form.newBrandDesign.email_label
                          : copy.form.email_label
                      }
                      touched={touched.email}
                      error={errors.email}
                      value={values.email}
                      autoComplete="none"
                      // eslint-disable-next-line jsx-a11y/no-autofocus
                      autoFocus
                      className={isNewBrand ? '' : 'do-u-mb--small'}
                    />
                    <TextInputComponent
                      placeholder={copy.form.login.password_placeholder}
                      required
                      name="password"
                      label={copy.form.password_label}
                      type="password"
                      touched={touched.password}
                      error={errors.password}
                      value={values.password}
                      autoComplete="none"
                      className={isNewBrand ? '' : 'do-u-mb--medium'}
                    />
                  </>
                )}
                <SpinnerButtonComponent
                  type="submit"
                  fullWidth={true}
                  disabled={isLoading || isRedirecting}
                  spinning={isLoading || isRedirecting}
                  loadingMessage={copy.loading.login.belowButton}
                >
                  {isNewBrand
                    ? copy.form.login.newBrandDesign.button_text
                    : copy.form.login.button_text}
                </SpinnerButtonComponent>
              </Form>
            )}

            {!hasPreferredLogin &&
              (isNewBrand ? (
                <NewFormHr />
              ) : (
                <FormHr>
                  <span>{copy.form.or}</span>
                </FormHr>
              ))}

            {(!hasPreferredLogin || hasPreferredGoogle) && (
              <GoogleButtonWrapperComponent>
                <GoogleButtonComponent
                  url={googleOauthUrl}
                  text={copy.form.oauth.login.google_button}
                  onClick={() => window.location.assign(googleOauthUrl)}
                />
              </GoogleButtonWrapperComponent>
            )}
            {(!hasPreferredLogin || hasPreferredGithub) && (
              <GithubButtonComponent
                url={githubOauthUrl}
                text={copy.form.oauth.login.github_button}
                onClick={() => window.location.assign(githubOauthUrl)}
              />
            )}

            {(!hasPreferredLogin || hasPreferredEmail) && (
              <ForgotPassword style={{ textAlign: 'left' }}>
                <LinkComponent
                  forceExternal={FORCE_EXTERNAL}
                  to="/forgot_password/new"
                  onClick={(e) => {
                    navigate('/forgot_password/new', {
                      state: { email: !hasPreferredLogin && values.email }
                    });
                    e.preventDefault();
                  }}
                >
                  {copy.form.forgot_password}
                </LinkComponent>
              </ForgotPassword>
            )}

            {hasPreferredLogin && (
              <NotYouWrap>
                {isNewBrand ? (
                  <span className="need-help-text">{copy.form.not_you}</span>
                ) : (
                  <strong>{copy.form.not_you}</strong>
                )}
                <br />
                <LinkComponent
                  to={window.location.pathname + window.location.search}
                  onClick={(e) => {
                    // Delete invite_code cookie when choosing to sign in as another user
                    deleteCookie(TEAM_INVITE_COOKIE);

                    navigate(
                      window.location.pathname + window.location.search,
                      {
                        state: { preferredLoginState: 'none' }
                      }
                    );

                    e.preventDefault();

                    // Reload /login with new state to not display preferred login
                    window.location.reload();
                  }}
                >
                  {copy.form.newBrandDesign.login_different_account}
                </LinkComponent>
              </NotYouWrap>
            )}
          </>
        )}
      </Formik>
    </WrapperComponent>
  );
};

export default LoginForm;
